<template>
  <div
    ref="root"
    class="menu-with-submenu min-width-calc w-full p-1 flex items-center h-7 rounded-md relative bg-white dark:bg-slate-700 justify-between hover:bg-woot-75 cursor-pointer dark:hover:bg-slate-800"
    :class="!subMenuAvailable ? 'opacity-50 cursor-not-allowed' : ''"
    @mouseenter="onRootEnter"
    @mouseleave="onRootLeave"
  >
    <div class="flex min-w-0 flex-1 items-center pr-1">
      <fluent-icon
        :icon="option.icon"
        size="14"
        class="menu-icon flex-shrink-0"
      />
      <p
        ref="rowLabelEl"
        v-tooltip.top="rowLabelTooltip"
        class="my-0 mx-2 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs"
      >
        {{ option.label }}
      </p>
    </div>
    <fluent-icon icon="chevron-right" size="12" class="flex-shrink-0" />
    <div
      v-show="hovered && subMenuAvailable"
      ref="submenu"
      class="submenu bg-white dark:bg-slate-700 p-1 pr-0.5 shadow-lg rounded-md absolute min-h-0 min-w-min overflow-y-scroll overflow-x-hidden cursor-pointer z-[10000] w-max max-w-[min(20rem,calc(100vw-1rem))] overscroll-y-contain"
      :class="submenuPositionClass"
      :style="submenuStyle"
    >
      <slot />
    </div>
  </div>
</template>

<script>
const SUBMENU_MAX_HEIGHT_PX = 240; // ~15rem, matches previous max-h-[15rem]
const VIEWPORT_MARGIN = 8;
/** Rough width so we open the flyout to the left when there is no room on the right */
const SUBMENU_MIN_WIDTH_ESTIMATE_PX = 200;

export default {
  props: {
    option: {
      type: Object,
      default: () => {},
    },
    subMenuAvailable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      hovered: false,
      alignBottom: false,
      openLeft: false,
      submenuMaxHeightPx: SUBMENU_MAX_HEIGHT_PX,
      rowLabelTooltip: '',
      rowLabelResizeObserver: null,
    };
  },
  computed: {
    submenuPositionClass() {
      if (this.openLeft) {
        return this.alignBottom
          ? 'right-full left-auto top-auto bottom-0'
          : 'right-full left-auto top-0 bottom-auto';
      }
      return this.alignBottom
        ? 'left-full right-auto top-auto bottom-0'
        : 'left-full right-auto top-0 bottom-auto';
    },
    submenuStyle() {
      return {
        maxHeight: `${this.submenuMaxHeightPx}px`,
      };
    },
  },
  watch: {
    option: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncRowLabelTooltip());
      },
    },
  },
  mounted() {
    this.bindRowLabelResizeObserver();
    this.$nextTick(() => this.syncRowLabelTooltip());
  },
  beforeDestroy() {
    this.unbindRowLabelResizeObserver();
  },
  methods: {
    bindRowLabelResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;
      this.rowLabelResizeObserver = new ResizeObserver(() => {
        this.syncRowLabelTooltip();
      });
      this.$nextTick(() => {
        const el = this.$refs.rowLabelEl;
        if (el) this.rowLabelResizeObserver.observe(el);
      });
    },
    unbindRowLabelResizeObserver() {
      if (this.rowLabelResizeObserver) {
        this.rowLabelResizeObserver.disconnect();
        this.rowLabelResizeObserver = null;
      }
    },
    syncRowLabelTooltip() {
      const el = this.$refs.rowLabelEl;
      const text = this.option.label;
      if (!el || text === undefined || text === null || text === '') {
        this.rowLabelTooltip = '';
        return;
      }
      const label = String(text);
      this.rowLabelTooltip = el.scrollWidth > el.clientWidth + 1 ? label : '';
    },
    onRootEnter() {
      if (!this.subMenuAvailable) return;
      this.hovered = true;
      this.$nextTick(() => {
        this.positionSubmenu();
        this.syncRowLabelTooltip();
      });
    },
    onRootLeave() {
      this.hovered = false;
    },
    positionSubmenu() {
      const root = this.$refs.root;
      if (!root) return;

      const r = root.getBoundingClientRect();
      const m = VIEWPORT_MARGIN;
      const cap = SUBMENU_MAX_HEIGHT_PX;

      const spaceDown = window.innerHeight - m - r.top;
      const spaceUp = r.bottom - m;

      if (spaceDown >= cap) {
        this.alignBottom = false;
      } else if (spaceUp >= cap) {
        this.alignBottom = true;
      } else {
        this.alignBottom = spaceUp > spaceDown;
      }

      const available = this.alignBottom ? spaceUp : spaceDown;
      this.submenuMaxHeightPx = Math.min(cap, Math.max(0, available));

      this.openLeft =
        r.right + SUBMENU_MIN_WIDTH_ESTIMATE_PX > window.innerWidth - m;
    },
  },
};
</script>

<style scoped lang="scss">
.menu-with-submenu {
  min-width: calc(6.25rem * 2);
}

/* Visible scrollbar when label/agent lists exceed max-height */
.submenu {
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #e2e8f0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #e2e8f0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #94a3b8;
    border-radius: 4px;

    &:hover {
      background-color: #64748b;
    }
  }
}
</style>

<style lang="scss">
html.dark .menu-with-submenu .submenu {
  scrollbar-color: #64748b #334155;

  &::-webkit-scrollbar-track {
    background: #334155;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #64748b;

    &:hover {
      background-color: #94a3b8;
    }
  }
}
</style>
